import { Clock3, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'))

function parseTime(value?: string): { h: string; m: string } | null {
  if (!value) return null
  const m = /^(\d{2}):(\d{2})$/.exec(value.trim())
  if (!m) return null
  const hh = Number(m[1])
  const mm = Number(m[2])
  if (hh > 23 || mm > 59) return null
  return { h: m[1]!, m: m[2]! }
}

function nearestMinuteOption(mm: string): string {
  const n = Number(mm)
  if (!Number.isFinite(n)) return '00'
  const rounded = Math.round(n / 5) * 5
  const clamped = Math.min(55, Math.max(0, rounded))
  return String(clamped).padStart(2, '0')
}

export type TimeFieldProps = {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  disabled?: boolean
  clearable?: boolean
  className?: string
  id?: string
  'aria-label'?: string
  'aria-invalid'?: boolean
  /** Dakika adımı: 5 (varsayılan) veya 1 */
  minuteStep?: 1 | 5
}

export function TimeField({
  value = '',
  onChange,
  placeholder,
  disabled,
  clearable = true,
  className,
  id,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  minuteStep = 5,
}: TimeFieldProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const parsed = parseTime(value)

  const minuteOptions = useMemo(() => {
    if (minuteStep === 1) return Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))
    return MINUTES
  }, [minuteStep])

  const draftH = parsed?.h ?? '09'
  const draftM = parsed ? (minuteStep === 5 ? nearestMinuteOption(parsed.m) : parsed.m) : '00'

  const label = parsed ? `${parsed.h}:${parsed.m}` : (placeholder ?? t('common.timePlaceholder'))

  function commit(h: string, m: string) {
    onChange?.(`${h}:${m}`)
  }

  return (
    <Popover open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <div
        className={cn(
          'relative flex h-9 w-full items-center rounded-md border border-input bg-card shadow-xs transition-colors',
          'focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/40',
          ariaInvalid && 'border-destructive ring-destructive/30',
          disabled && 'opacity-50',
          className,
        )}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-invalid={ariaInvalid || undefined}
            className={cn(
              'inline-flex h-full min-w-0 flex-1 items-center gap-2 rounded-md bg-transparent px-3 text-left text-sm outline-none',
              'hover:bg-muted/30',
              'disabled:cursor-not-allowed',
              !parsed && 'text-muted-foreground',
            )}
          >
            <Clock3 className="size-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate font-medium tabular-nums">{label}</span>
          </button>
        </PopoverTrigger>
        {clearable && parsed && !disabled ? (
          <button
            type="button"
            className="mr-1.5 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => onChange?.('')}
            aria-label={t('common.clear')}
          >
            <X className="size-3.5" />
          </button>
        ) : null}
      </div>

      <PopoverContent align="start" className="w-[220px] p-0">
        <div className="border-b border-border/70 px-3 py-2">
          <div className="text-center text-sm font-semibold tabular-nums tracking-tight">
            {draftH}:{draftM}
          </div>
          <p className="text-center text-[11px] text-muted-foreground">{t('common.timeHint')}</p>
        </div>
        <div className="grid grid-cols-2 gap-0">
          <div className="border-r border-border/70">
            <div className="px-2 py-1.5 text-center text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
              {t('common.hour')}
            </div>
            <div className="scrollbar-none max-h-48 overflow-y-auto p-1.5">
              {HOURS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => commit(h, draftM)}
                  className={cn(
                    'flex w-full items-center justify-center rounded-lg py-1.5 text-sm tabular-nums transition-colors hover:bg-muted',
                    draftH === h && 'bg-primary font-semibold text-primary-foreground hover:bg-primary',
                  )}
                >
                  {h}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="px-2 py-1.5 text-center text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
              {t('common.minute')}
            </div>
            <div className="scrollbar-none max-h-48 overflow-y-auto p-1.5">
              {minuteOptions.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => commit(draftH, m)}
                  className={cn(
                    'flex w-full items-center justify-center rounded-lg py-1.5 text-sm tabular-nums transition-colors hover:bg-muted',
                    draftM === m && 'bg-primary font-semibold text-primary-foreground hover:bg-primary',
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end border-t border-border/70 px-2 py-2">
          <Button type="button" size="sm" className="h-8 rounded-lg" onClick={() => setOpen(false)}>
            {t('common.confirm')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
